// src/components/scene/HubScene.jsx
import { useEffect, useRef, useState } from "react";
import { Html, OrbitControls, useAnimations, useGLTF } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { gsap } from "gsap";
import DesktopUI from "../ui/DesktopUI/desktopUI";
import CameraManager from "../manager/CameraManager";
import useFlowStore from "../store/flowStore";
import useCursorStore from "../store/cursorStore";
import useHoverRaycast from "../../hooks/useHoverRaycast";
import { audios, subtitles } from "../../assets/assets";


export default function HubScene({
  interactUIRef,
  onSceneExit,
  onReady,
  visible = true,
}) {
  const uiWrapperRef = useRef();
  const screenRef = useRef();
  const groupRef = useRef();

  const [screenPos, setScreenPos] = useState(null);
  const [screenQuat, setScreenQuat] = useState(null);
  const [desktopClickDone, setDesktopClickDone] = useState(false);

  const [lectureAudioEnd, setLectureAudioEnd] = useState(false);

  // const glb = useGLTF("./models/hub_v3.glb");
  const glb = useGLTF("./models/hub_baked_1.glb");
  const { scene, animations } = glb;
  const anim = useAnimations(animations, scene);

  const { set } = useThree();

  const {
    nextStep,
    playCurrentAudio,
    visibleScenes,
    showDesktopInteractUI,
    showInterface,
    setShowInterface,
    playAudioFile,
    desktopIsClickable,
  } = useFlowStore.getState();
  const { setCursorScale, setCursorTheme, setCursorInteractive } =
    useCursorStore.getState();

  const currentStep = useFlowStore((s) => s.currentStep);

  useHoverRaycast();

  useEffect(() => {
    if (!scene) return;

    setCursorInteractive(false);

    onReady?.();
  }, [scene]);

  useEffect(() => {
    const screen = scene?.getObjectByName("dalle_for_bake");
    if (!screen) return;

    screenRef.current = screen;

    screen.updateWorldMatrix(true, true);

    const worldPos = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    screen.getWorldPosition(worldPos);
    screen.getWorldQuaternion(worldQuat);

    // Correction quaternion ici (ajuste l'axe et l'angle si besoin)
    const correction = new THREE.Quaternion();
    correction.setFromAxisAngle(new THREE.Vector3(1, 1, 1), -Math.PI / 2);
    worldQuat.multiply(correction);

    setScreenPos(worldPos.toArray());
    setScreenQuat(worldQuat);
    
    const doorAnim = scene.getObjectByName("module_porte_lilian_anim_for_bake");
    doorAnim.children.forEach(child => {
      child.visible = false;
    });

    const doorVierge = scene.getObjectByName("module_porte_vierge");
    doorVierge.visible = true;
    // put a light on door

  }, [scene]);

  useEffect(() => {
    if (scene && anim?.actions) {
      const { visibleScenes } = useFlowStore.getState();

      if (!visibleScenes.hub) return;

      CameraManager.register({
        scene,
        animations: anim,
        set,
        onReady: () => {
          CameraManager.switchCamera("camera_1");
          CameraManager.disableMouseLook();
        },
      });
    }
  }, [scene, anim, set, visibleScenes.secondaryScene]);

  // ACTIVATE DESKTOP INTERACT UI AND HITBOX AT THE END OF INTRO AUDIO
  useEffect(() => {
    if (!scene) return;
    // Toutes ces actions sont maintenant ici
    if (useFlowStore.getState().currentStep === 1) {
      const desktopHitbox = scene.getObjectByName("module_desk_hitbox");
      if (desktopHitbox) {
        useFlowStore.getState().setShowInteractUI(true); // Pour DesktopUI
        console.log("Desktop interact UI activated");
        setCursorInteractive(true);
        desktopHitbox.userData.isClickable = desktopIsClickable;
        if (desktopHitbox.userData.isClickable) {
          desktopHitbox.userData.onClick = handleDesktopClick;
        }
      }
    }
  }, [scene, desktopIsClickable]);

  useEffect(() => {
    if (showInterface) {
      setTimeout(() => {
        if (uiWrapperRef.current) {
          uiWrapperRef.current.classList.remove("fade-out-ui");
          uiWrapperRef.current.classList.add("fade-in-ui");
        }
      }, 0);
    } else {
      // Si tu veux, tu peux enlever fade-in, et ajouter fade-out à la fermeture
      if (uiWrapperRef.current) {
        uiWrapperRef.current.classList.remove("fade-in-ui");
        uiWrapperRef.current.classList.add("fade-out-ui");
      }
    }
  }, [scene, showInterface]);

  useEffect(() => {
    if (!scene) return;

    const desktopHitbox = scene.getObjectByName("module_desk_hitbox");
    const step = useFlowStore.getState().flowSteps[currentStep];
    if (step?.label === "HubStart") {
      const turnAudio = new Audio(
        audios.swoosh_intro_cloud
      );
      turnAudio.play().catch(console.warn);

      // PLAY CAMERA_1 ANIMATION : GOING TO HUB LARGE ANGLE
      let playAnimationOnce = false;
      CameraManager.playAnimation(() => {
        if (playAnimationOnce) return;
        playAnimationOnce = true;

        // ENABLE FREE MOUSE LOOK
        CameraManager.reset();
        CameraManager.switchCamera("camera_2");

        setTimeout(() => {
          CameraManager.enableMouseLook(1);
        }, 50);

        // SHOW DESKTOP UI
        setShowInterface(true);
        setTimeout(() => {
          if (uiWrapperRef.current)
            uiWrapperRef.current.classList.add("fade-in-ui");
        }, 100);
      }, 4000);
    }
  }, [scene, currentStep]);

  useEffect(() => {
    const desktopHitbox = scene.getObjectByName("module_desk_hitbox");
    const doorHitbox = scene.getObjectByName("door_module_hitbox");
    const ecran = scene.getObjectByName("dalle_for_bake");
    // LIGHT THEME BY DEFAULT
    // setCursorTheme("light");

    // DESKTOP CLICKING
    if (desktopHitbox) {
      desktopHitbox.visible = true;
      desktopHitbox.frustumCulled = false;
      desktopHitbox.scale.set(1, 1.5, 1);
      desktopHitbox.material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      desktopHitbox.raycast = THREE.Mesh.prototype.raycast;

      // DESKTOP HITBOX NOT CLICKABLE BY DEFAULT
      desktopHitbox.userData.isClickable = false;
      desktopHitbox.userData.onPointerOver = () => setCursorScale(3.5);
      desktopHitbox.userData.onPointerOut = () => setCursorScale(1);
    }

    // DOOR CLICKING
    if (doorHitbox) {
      doorHitbox.visible = true;
      doorHitbox.frustumCulled = false;
      doorHitbox.material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      doorHitbox.raycast = THREE.Mesh.prototype.raycast;
      doorHitbox.userData.isClickable = false;
      setCursorInteractive(true);

      doorHitbox.userData.onPointerOver = () => setCursorScale(2.5);
      doorHitbox.userData.onPointerOut = () => setCursorScale(1);

      // PERMIT TO CLICK ONLY ONCE
      let hasClicked = false;
      doorHitbox.userData.onClick = async () => {
        // PERMIT TO CLICK ONLY ONCE
        console.log("CLICKED"); 
        if (hasClicked) return;
        hasClicked = true;

        // DISABLE HOVER ON DOOR HITBOX
        doorHitbox.userData.onPointerOver = () => setCursorScale(1);
        doorHitbox.userData.onPointerOut = () => setCursorScale(1);

        // HIDE INTERACT UI
        if (interactUIRef?.current) {
          await interactUIRef?.current?.exit();
        }

        // RESET FREE LOOK AT
        CameraManager.resetLookTargetWithAnimation({
          duration: 1,
          onComplete: () => {
            // PLAY CAMERA ANIMATION : GOING TO DOOR
            let playAnimationOnce = false;
            nextStep(
              () => {
                if (playAnimationOnce) return;
                playAnimationOnce = true;

                // PLAY GOING TO DOOR ANIMATION FROM 100 / 30 SECONDS
                CameraManager.playAnimationFrom({
                  startTime: 120 / 30, // 100 frames at 30 FPS
                });

                setTimeout(() => {
                  nextStep(() => {
                    console.log("SHOW TRANSITION UI !");
                  });
                  // HIDE DESKTOP UI
                  if (uiWrapperRef.current) {
                    uiWrapperRef.current.classList.remove("fade-in-ui");
                    uiWrapperRef.current.classList.add("fade-out-ui");
                  }
                }, 5000);

                // PLAY SOUND DESIGN GOING TO DOOR
                const turnAudio = new Audio(audios.door_opening);
                turnAudio.play().catch(console.warn);
              }
            );
          },
        });
      };
    }

    if (ecran) screenRef.current = ecran;
  }, [scene]);

  // GET SCREEN POSITION AND QUATERNION
  useEffect(() => {
    if (screenRef.current) {
      const box = new THREE.Box3().setFromObject(screenRef.current);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);
      setScreenPos(center.toArray());
    }
  }, [scene]);


  const handleDesktopClick = async () => {
    if (desktopClickDone) return;
    setDesktopClickDone(true);

    const desktopHitbox = scene?.getObjectByName("module_desk_hitbox");
    if (desktopHitbox) {
      desktopHitbox.userData.onPointerOver = () => setCursorScale(1);
      desktopHitbox.userData.onPointerOut = () => setCursorScale(1);
    }

    // HIDE INTERACT UI
    if (interactUIRef?.current) {
      await interactUIRef?.current?.exit();
    }

    const swooshAudio = new Audio(audios.swoosh_computer_in);
    swooshAudio.play().catch(console.warn);

    // GOING STEP 2
    nextStep(() => {
      CameraManager.resetLookTargetWithAnimation({
        duration: 1,
        onComplete: () => {
          let playAnimationOnce = false;
          CameraManager.playAnimation(() => {
            if (playAnimationOnce) return;
            playAnimationOnce = true;

            CameraManager.reset();
            CameraManager.switchCamera("camera_3");

            // Dark cursor for desktop UI
            setCursorTheme("dark");

            // Play mail Audio
            setTimeout(() => {
              CameraManager.enableMouseLook();
            }, 50);

            playCurrentAudio(() => {
              setTimeout(() => {
                playAudioFile(
                  audios.fragments_etoiles,
                  subtitles.fragments_etoiles,
                  () => {
                    setLectureAudioEnd(true);
                    useFlowStore.getState().setShowInteractUI(true);
                  }
                );
              }, 1000);
            });
          }, 5000);
        },
      });
    });
  };

  const handleClick = (e) => {
    e.stopPropagation();
    const clicked = e.object;
    if (clicked.userData.isClickable && clicked.userData.onClick) {
      clicked.userData.onClick();
    }
  };


  return (
    <group ref={groupRef} visible={visible}>
      {/* <OrbitControls /> */}
      <primitive object={scene} onPointerDown={handleClick} />
      {showInterface && screenPos && (
        <mesh geometry={screenRef.current?.geometry}>
          <Html
            position={screenPos}
            quaternion={screenQuat}
            transform
            distanceFactor={0.3}
            scale={[0.975, 1.02, 1]}
            wrapperClass="no-native-cursor"
          >
            <div ref={uiWrapperRef} className="desktopUI__fadeWrapper">
              <DesktopUI
                scene={scene}
                audioEnd={lectureAudioEnd}
                onDesktopClick={!desktopClickDone ? handleDesktopClick : undefined}
              />
            </div>
          </Html>
        </mesh>
      )}
    </group>
  );
}
